import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../atoms/Button';
import Card from '../../atoms/Card';
import { OrderOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import './BartenderOrderList.scss';

interface BartenderOrderListProps {
  eventId?: number;
  // Bumped by the parent to force a reload (e.g. when coming back from an order)
  refreshToken?: number;
  openingOrderId?: number | null;
  onOrderSelect: (order: OrderOut) => void;
  // Called on Enter with a code that is not an order in the list, i.e. what
  // a hardware QR/barcode reader types into the search box
  onCodeEntered: (code: string) => void;
  onScanClick: () => void;
  className?: string;
}

const PAGE_SIZE = 50;

const BartenderOrderList: React.FC<BartenderOrderListProps> = ({
  eventId,
  refreshToken = 0,
  openingOrderId = null,
  onOrderSelect,
  onCodeEntered,
  onScanClick,
  className = ''
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { newOrderEvent, connected, connectionId } = useNotifications();
  const [orders, setOrders] = useState<OrderOut[]>([]);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const isFetchingRef = useRef(false);
  // An update that arrives mid-fetch must not be lost: run one more fetch after.
  const refetchPendingRef = useRef(false);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const fetchOrders = useCallback(
    async (options?: { showLoader?: boolean; manual?: boolean }) => {
      if (isFetchingRef.current) {
        refetchPendingRef.current = true;
        return;
      }
      isFetchingRef.current = true;
      if (options?.showLoader) setIsLoading(true);
      if (options?.manual) setIsRefreshing(true);
      setError(null);
      try {
        const queue = await apiService.getBartenderQueue({ skip: 0, limit });
        setOrders(queue);
        setLastUpdatedAt(new Date());
      } catch (fetchError) {
        console.error('Failed to fetch order queue:', fetchError);
        setError(t('bartender.orders.fetchError'));
      } finally {
        isFetchingRef.current = false;
        if (options?.showLoader) setIsLoading(false);
        if (options?.manual) setIsRefreshing(false);
      }
      if (refetchPendingRef.current) {
        refetchPendingRef.current = false;
        fetchOrdersRef.current();
      }
    },
    [limit, t]
  );
  const fetchOrdersRef = useRef<(options?: { showLoader?: boolean; manual?: boolean }) => Promise<void>>(async () => {});
  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    fetchOrders({ showLoader: orders.length === 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user?.event_venue_id, refreshToken, fetchOrders]);

  // Real-time refresh: an order was created, opened by another bartender,
  // released, delivered or cancelled for this event.
  useEffect(() => {
    if (!newOrderEvent) return;
    if (eventId && newOrderEvent.eventId !== eventId) return;
    fetchOrders();
  }, [newOrderEvent, eventId, fetchOrders]);

  // After the socket (re)connects, catch up on anything missed while it was down
  const seenConnectionIdRef = useRef(connectionId);
  useEffect(() => {
    if (connectionId === seenConnectionIdRef.current) return;
    seenConnectionIdRef.current = connectionId;
    fetchOrders();
  }, [connectionId, fetchOrders]);

  // Safety net: refresh when the screen comes back to the foreground, and poll
  // while the real-time connection is down so the queue never goes stale.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchOrders();
    };
    document.addEventListener('visibilitychange', onVisible);
    const poll = connected ? undefined : setInterval(() => {
      if (document.visibilityState === 'visible') fetchOrders();
    }, 15000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (poll) clearInterval(poll);
    };
  }, [connected, fetchOrders]);

  const normalizedQuery = query.trim();
  const filteredOrders = /^\d+$/.test(normalizedQuery)
    ? orders.filter((order) => String(order.id).includes(normalizedQuery))
    : orders;

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !normalizedQuery) return;
    e.preventDefault();
    const exactMatch = orders.find((order) => String(order.id) === normalizedQuery);
    if (exactMatch) {
      onOrderSelect(exactMatch);
    } else {
      onCodeEntered(normalizedQuery);
    }
    setQuery('');
  };

  const itemsSummary = (order: OrderOut) =>
    order.items.map((item) => `${item.quantity}x ${item.drink_name || item.food_name}`).join(', ');

  const hasMore = orders.length === limit;

  return (
    <Card className={`bartender-order-list ${className}`}>
      <div className="bartender-order-list__header">
        <h3 className="bartender-order-list__title">
          {t('bartender.orders.title')}
          {!isLoading && <span className="bartender-order-list__count">{orders.length}{hasMore ? '+' : ''}</span>}
        </h3>
        <div className="bartender-order-list__refresh">
          {lastUpdatedAt && (
            <span className="bartender-order-list__updated-at">
              {t('bartender.orderDetail.updated')}: {lastUpdatedAt.toLocaleTimeString()}
            </span>
          )}
          <Button
            onClick={() => fetchOrders({ manual: true })}
            size="sm"
            variant="secondary"
            disabled={isRefreshing || isLoading}
            loading={isRefreshing}
          >
            {isRefreshing ? t('common.loading') : t('common.refresh')}
          </Button>
        </div>
      </div>

      <div className="bartender-order-list__toolbar">
        <input
          ref={searchRef}
          id="bartender-order-search"
          type="text"
          autoComplete="off"
          className="bartender-order-list__search-input"
          placeholder={t('bartender.orders.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <Button
          variant="primary"
          size="md"
          onClick={onScanClick}
          className="bartender-order-list__scan-btn"
        >
          {t('bartender.orders.scanCode')}
        </Button>
      </div>

      {error && <div className="bartender-order-list__error">{error}</div>}

      <div className="bartender-order-list__content">
        {isLoading ? (
          <div className="bartender-order-list__loading">{t('bartender.orders.loading')}</div>
        ) : filteredOrders.length === 0 ? (
          <div className="bartender-order-list__empty">{t('bartender.orders.noOrders')}</div>
        ) : (
          <div className="bartender-order-list__grid">
            {filteredOrders.map((order) => {
              const isCashDue = order.status === 'pending';
              const isMine = !!user && order.claimed_by_id === user.id;
              const isOpening = openingOrderId === order.id;
              return (
                <button
                  key={order.id}
                  type="button"
                  className={[
                    'bartender-order-list__item',
                    isMine ? 'bartender-order-list__item--mine' : '',
                    isOpening ? 'bartender-order-list__item--opening' : ''
                  ].join(' ')}
                  onClick={() => onOrderSelect(order)}
                  disabled={openingOrderId !== null}
                  title={itemsSummary(order)}
                >
                  <span className="bartender-order-list__item-id">#{order.id}</span>
                  <span className="bartender-order-list__item-summary">{itemsSummary(order)}</span>
                  {(isCashDue || isMine) && (
                    <span className="bartender-order-list__item-tags">
                      {isCashDue && (
                        <span className="bartender-order-list__tag bartender-order-list__tag--cash">
                          {t('bartender.orders.cashDue')}
                        </span>
                      )}
                      {isMine && (
                        <span className="bartender-order-list__tag bartender-order-list__tag--mine">
                          {t('bartender.orders.openedByYou')}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!isLoading && hasMore && (
          <div className="bartender-order-list__load-more">
            <Button variant="ghost" size="sm" onClick={() => setLimit((prev) => prev + PAGE_SIZE)}>
              {`${t('common.show')} ${t('common.more')}`}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BartenderOrderList;
